import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUserProfile } from "@/hooks/useCurrentUserProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate, useLocation } from "react-router-dom";
import { UserAvatar } from "@/components/UserAvatar";
import logoIcon from "@/assets/logo-icon.jpg";
import { Menu, LogOut, Upload, Activity, BarChart3, Map, User, Settings, MessageSquare, Calendar, Lock } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import UpgradeDialog from "@/components/UpgradeDialog";

const AuthenticatedNav = () => {
  const { user, signOut } = useAuth();
  const { profile, featuredVehicle } = useCurrentUserProfile();
  const { canAccess } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const canAccessEvents = canAccess('organizer');

  // Main navigation items (desktop) - Events handled separately due to locking
  const mainNavItems = [{
    label: "Activity",
    path: "/activity",
    icon: Activity
  }, {
    label: "Dashboard",
    path: "/dashboard",
    icon: BarChart3
  }, {
    label: "Explore",
    path: "/explore",
    icon: Map
  }, {
    label: "Feedback",
    path: "/feedback",
    icon: MessageSquare
  }];

  // Mobile includes Profile and Settings
  const mobileNavItems = [...mainNavItems, {
    label: "Profile",
    path: `/profile/${user?.id}`,
    icon: User
  }, {
    label: "Settings",
    path: "/settings",
    icon: Settings
  }];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  // Use featured vehicle photo, then avatar_url, then null for initials fallback
  const avatarImageUrl = featuredVehicle?.primary_photo_url || profile?.avatar_url || null;

  return (
    <nav className="flex items-center justify-between w-full">
      {/* Logo */}
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
        <img src={logoIcon} alt="Airing Down Logo" className="w-10 h-10 rounded-lg" />
        <div>
          <h1 className="text-xl font-bold text-foreground">Airing Down</h1>
          <p className="text-xs text-muted-foreground">
            Off-Road Adventures, Mapped <span className="text-primary font-medium">· Beta</span>
          </p>
        </div>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-1">
        {mainNavItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <button 
              key={item.path} 
              onClick={() => navigate(item.path)} 
              className={`relative px-4 py-2 text-sm transition-colors ${
                isActive 
                  ? 'text-foreground font-semibold' 
                  : 'text-muted-foreground hover:text-foreground font-medium'
              }`}
            >
              {item.label}
              {isActive && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          );
        })}
        
        {/* Locked Events Tab */}
        {canAccessEvents ? (
          <button 
            onClick={() => navigate('/events')} 
            className={`relative px-4 py-2 text-sm transition-colors ${
              location.pathname === '/events'
                ? 'text-foreground font-semibold' 
                : 'text-muted-foreground hover:text-foreground font-medium'
            }`}
          >
            Events
            {location.pathname === '/events' && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={(e) => { e.preventDefault(); setUpgradeDialogOpen(true); }}
                  className="relative px-4 py-2 text-sm text-muted-foreground/50 font-medium flex items-center gap-1 cursor-not-allowed"
                >
                  <Lock className="h-3 w-3" />
                  Events
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Upgrade to Organizer to access Events</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <div className="flex items-center gap-3 ml-4">
          <Button variant="hero" size="sm" onClick={() => navigate('/log-run')} className="text-sm font-medium">
            <Upload className="h-4 w-4 mr-2" />
            Log a Run
          </Button>
          
          <div className="flex items-center gap-3 pl-3 border-l border-border">
            <NotificationBell />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative h-8 w-8 rounded-full p-0">
                  <UserAvatar
                    username={profile?.username}
                    avatarUrl={avatarImageUrl}
                    fallbackEmail={user?.email}
                    size="sm"
                    usePhotoFilter={!!featuredVehicle?.primary_photo_url}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">Account</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(`/profile/${user?.id}`)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <img src={logoIcon} alt="Airing Down Logo" className="w-8 h-8 rounded-lg" />
                <div>
                  <h2 className="text-lg font-bold text-foreground">Airing Down</h2>
                  <p className="text-xs text-muted-foreground">
                    Off-Road Adventures, Mapped <span className="text-primary font-medium">· Beta</span>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-4 flex-1">
              {mobileNavItems.slice(0, 4).map(item => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Button 
                    key={item.path} 
                    variant="ghost" 
                    onClick={() => handleNavigation(item.path)} 
                    className={`justify-start text-left ${
                      isActive ? 'bg-primary/10 text-primary border-l-2 border-primary' : ''
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-3" />
                    {item.label}
                  </Button>
                );
              })}
              
              {/* Locked Events in mobile */}
              {canAccessEvents ? (
                <Button 
                  variant="ghost" 
                  onClick={() => handleNavigation('/events')} 
                  className={`justify-start text-left ${
                    location.pathname === '/events' ? 'bg-primary/10 text-primary border-l-2 border-primary' : ''
                  }`}
                >
                  <Calendar className="h-4 w-4 mr-3" />
                  Events
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  onClick={() => { setIsOpen(false); setUpgradeDialogOpen(true); }}
                  className="justify-start text-left text-muted-foreground/50"
                >
                  <Lock className="h-4 w-4 mr-3" />
                  Events
                  <span className="ml-auto text-xs">Organizer</span>
                </Button>
              )}
              
              <Separator className="my-2" />
              
              {mobileNavItems.slice(4).map(item => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Button 
                    key={item.path} 
                    variant="ghost" 
                    onClick={() => handleNavigation(item.path)} 
                    className={`justify-start text-left ${
                      isActive ? 'bg-primary/10 text-primary border-l-2 border-primary' : ''
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-3" />
                    {item.label}
                  </Button>
                );
              })}
            </div>
            
            <div className="flex flex-col gap-3 mt-8">
              <Button variant="hero" onClick={() => handleNavigation('/log-run')} className="w-full">
                <Upload className="h-4 w-4 mr-2" />
                Log a Run
              </Button>
              <Button variant="outline" onClick={handleSignOut} className="w-full">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <UpgradeDialog
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        targetTier="organizer"
        featureContext="Events"
      />
    </nav>
  );
};

export default AuthenticatedNav;
